import { useCallback, useEffect, useState } from 'react';
import { UmsatzWert } from '@/components/UmsatzSperre';
import { toast } from 'sonner';
import { Loader2, Target, Save } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { kpiAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/* Kennzahlen (v6, M8): Zeitreihe aus kpi_snapshots — Mitglieder + MRR über
   12 Monate, dieselbe Quelle wie die Kacheln auf /heute. Ziele pflegbar. */

function euro(v) { return (v ?? 0).toLocaleString('de-DE', { maximumFractionDigits: 0 }) + ' €'; }

export default function KennzahlenPage() {
  const { user, isAdminPermissions } = useAuth();
  const [punkte, setPunkte] = useState(null);
  const [monate, setMonate] = useState(12);
  const [ziele, setZiele] = useState(null);
  const [speichert, setSpeichert] = useState(false);
  const istLeitung = user?.role === 'admin' || user?.role === 'manager' || isAdminPermissions;

  const lade = useCallback(async () => {
    try {
      const [z, r] = await Promise.all([kpiAPI.ziele(), kpiAPI.zeitreihe(monate)]);
      setZiele(z.data);
      setPunkte(r.data.punkte.map(p => ({ ...p, datum_kurz: p.datum.slice(8, 10) + '.' + p.datum.slice(5, 7) + '.' })));
    } catch { toast.error('Kennzahlen konnten nicht geladen werden'); }
  }, [monate]);
  useEffect(() => { lade(); }, [lade]);

  const speichern = async () => {
    setSpeichert(true);
    try { setZiele((await kpiAPI.zieleSetzen(umsatzGesperrt ? { mitglieder: +ziele.mitglieder } : { mitglieder: +ziele.mitglieder, mrr: +ziele.mrr, nob: +ziele.nob })).data); toast.success('Ziele gespeichert'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setSpeichert(false); }
  };

  if (!punkte) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  const letzter = punkte[punkte.length - 1];
  // Umsatz-Sperre (30.10.2026): ohne Freigabe liefert der Server MRR/NOB als null — dann keine Linie und keine Eingabe
  const umsatzGesperrt = !!ziele && ziele.mrr == null && ziele.nob == null;

  return (
    <div className="space-y-6" data-testid="kennzahlen-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Kennzahlen</h1>
          <p className="text-sm text-muted-foreground">Nächtlicher Snapshot · {punkte.length} Tage erfasst{letzter ? ` · Stand ${letzter.datum}` : ''}</p>
        </div>
        <div className="flex gap-1">
          {[3, 6, 12, 24].map(m => (
            <Button key={m} size="sm" variant={monate === m ? 'default' : 'outline'} onClick={() => setMonate(m)}>{m} Monate</Button>
          ))}
        </div>
      </div>

      <Card className="border-border/60">
        <CardContent className="p-4">
          {punkte.length < 2 ? (
            <p className="text-sm text-muted-foreground py-10 text-center">Die Zeitreihe beginnt mit dem ersten Nacht-Snapshot — ab morgen erscheint hier eine Linie.</p>
          ) : (
            <div style={{ width: '100%', height: 340 }}>
              <ResponsiveContainer>
                <LineChart data={punkte} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="datum_kurz" tick={{ fontSize: 11 }} minTickGap={24} />
                  <YAxis yAxisId="links" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis yAxisId="rechts" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                  <Tooltip formatter={(v, name) => [name === 'MRR' ? euro(v) : v, name]} labelFormatter={(l, p) => p?.[0]?.payload?.datum || l} />
                  <Legend />
                  <Line yAxisId="links" type="monotone" dataKey="mitglieder" name="Mitglieder" stroke="#ef7c23" strokeWidth={2} dot={false} />
                  {!umsatzGesperrt && <Line yAxisId="rechts" type="monotone" dataKey="mrr" name="MRR" stroke="#1c1a17" strokeWidth={2} dot={false} />}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {ziele && (
        <Card className="border-border/60">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2"><Target className="w-4 h-4 text-primary" /><h2 className="font-semibold">Ziele</h2>
              <span className="text-xs text-muted-foreground">gelten für die Kacheln auf „Heute"</span></div>
            <div className="grid sm:grid-cols-3 gap-3">
              {[['mitglieder', 'Mitglieder'], ['mrr', 'MRR (€ / Monat)'], ['nob', 'Abschlüsse NOB (€ / Monat)']].map(([k, l]) => (
                <label key={k} className="text-sm space-y-1">
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">{l}</span>
                  {umsatzGesperrt && k !== 'mitglieder' ? <div><UmsatzWert wert={null} /></div> : <Input type="number" value={ziele[k] ?? ''} disabled={!istLeitung} onChange={e => setZiele(z => ({ ...z, [k]: e.target.value }))} data-testid={`ziel-${k}`} />}
                </label>
              ))}
            </div>
            {istLeitung && <Button size="sm" onClick={speichern} disabled={speichert} className="gap-1.5">{speichert ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Speichern</Button>}
          </CardContent>
        </Card>
      )}

      {letzter && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          {[['Leads (Woche)', letzter.leads_woche], ['Probetrainings gebucht', letzter.probetrainings_gebucht], ['Abschlüsse (Monat)', letzter.abschluesse_anzahl], ['Kündigungen 30 Tage', letzter.kuendigungen_30]].map(([l, v]) => (
            <Card key={l} className="border-border/60"><CardContent className="p-3"><p className="text-[11px] uppercase tracking-wider text-muted-foreground">{l}</p><p className="text-xl font-bold">{v ?? '–'}</p></CardContent></Card>
          ))}
        </div>
      )}
    </div>
  );
}
